import { useEffect } from 'react';
import moment from 'moment';
import axios from 'axios';
import _ from 'lodash';
import log from 'loglevel';
import { useSelector } from 'react-redux';

log.setLevel(process.env.NODE_ENV === 'production' ? 'warn' : 'debug');

// Query: Customer Activity by Persona
async function getPersonas(account, startDate, endDate) {
  log.debug('getPersonas called with account:', account);
  if (!account || !account.active || !account.config) {
    log.warn('Invalid account or configuration');
    return;
  }

  // Set events API
  const eventsApiUrl = `https://app.posthog.com/api/projects/${account.config.events.project}/insights/trend/`;

  // Get persona stats
  const personasQuery = {
    events: [
      {
        id: 'Customer Activity',
        math: 'dau',
        name: 'Customer Activity',
        type: 'events',
        order: 0,
        custom_name: 'Total Customer Created',
        properties: [
          {
            key: `properties.persona.version = '${
              account.active
                            && account.active.models
                            && account.active.models.personas
                ? account.active.models.personas.version
                : '1.0.0'
            }'`,
            type: 'hogql',
            value: null
          },
          {
            key: 'clientId',
            type: 'event',
            value: [`${account.active.id}`],
            operator: 'exact'
          }
        ]
      }
    ],
    date_to: endDate || null,
    display: 'ActionsTable',
    insight: 'TRENDS',
    interval: 'month',
    breakdown: 'properties.persona.ref.id',
    date_from: startDate || 'all',
    entity_type: 'event',
    breakdown_type: 'hogql'
    // "refresh": (moment().minutes() < 10) // Refresh if before 10 minutes of new hour
  };

    console.log("********getPersonas()", personasQuery)

  // Execute query
  let personasResponse = null;
  try {
    // Query
    personasResponse = await axios.post(eventsApiUrl, personasQuery, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${account.config.events.api}`
      }
    });
  } catch (error) {
    console.log('ERROR: ', error);
    return;
  }

    console.log("********getPersonas().resp", personasResponse.data)


  // Parse persona data
  const personaData = [];
  if (personasResponse.data && personasResponse.data.result.length > 0) {
    for (let i = 0; i < personasResponse.data.result.length; i++) {
      // Extract persona
      personaData.push({
        id: personasResponse.data.result[i].breakdown_value,
        total: personasResponse.data.result[i].aggregated_value
      });
    }
  }

  // Return values
  return {
    personas: personaData
  };
}

async function getPersonaActivity(
  account,
  knowledge,
  startDate,
  endDate,
  callback
) {
  // Set events API
  const eventsApiUrl = `https://app.posthog.com/api/projects/${account.config.events.project}/insights/trend/`;
  console.log('*********getPersonaActivity()', startDate, endDate);

  // Define query data
  const query = {
    date_from: startDate,
    date_to: endDate,
    events: [
      {
        id: 'Customer Activity',
        math: 'dau',
        name: 'Customer Activity',
        type: 'events',
        order: 0,
        properties: [
          {
            key: `properties.persona.version = '${
              account.active
                            && account.active.models
                            && account.active.models.personas
                ? account.active.models.personas.version
                : '1.0.0'
            }'`,
            type: 'hogql',
            value: null
          },
          {
            key: 'clientId',
            type: 'event',
            value: [`${account.active.id}`],
            operator: 'exact'
          }
        ]
      }
    ],
    interval: 'day',
    breakdown: 'properties.persona.name',
    entity_type: 'events',
    breakdown_type: 'hogql'
    // "refresh": (moment().minutes() < 1) // Refresh if before 10 minutes of new hour
  };

    console.log('*********getPersonaActivity()', startDate, endDate);


  // Execute query
  try {
    const response = await axios.post(eventsApiUrl, query, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${account.config.events.api}`
      }
    });

      console.log('*********getPersonaActivity().resp', response.data);

    return callback(knowledge, response.data);
  } catch (error) {
    console.log(`ERROR: ${error}`);
    // TODO: figure out what to return here
    return [];
  }
}

function personaActivityCallback(knowledge, data) {
  console.log('Persona Activity Callback:', data);

  // Format data
  const updatedPersonaActivities = [];
  for (let i = 0; i < data.result.length; i++) {
    // Get value
    const personaName = data.result[i].breakdown_value;

    // Calculate ranges
    const total30DayTarget = _.sum(
      data.result[i].data.slice(
        Math.max(data.result[i].data.length - 180, 0)
      )
    );
    const total30DayPrevious = _.sum(
      data.result[i].data.slice(
        Math.max(data.result[i].data.length - 210, 0),
        Math.max(data.result[i].data.length - 30, 0)
      )
    );

    // Fine matching Persona
    const matchedPersona = _.find(knowledge.entities, {
      name: personaName
    });

    // Setup values
    updatedPersonaActivities.push({
      id: matchedPersona ? matchedPersona.id : '',
      name: personaName,
      matches: total30DayTarget,
      delta:
                total30DayPrevious > 0
                  ? (100.0 * (total30DayTarget - total30DayPrevious))
                      / total30DayPrevious
                  : 100.0
    });
  }

  // Set new persona
  return updatedPersonaActivities;
}

export default async function usePersonas(
  activeChartData,
  startDate = null,
  endDate = null
) {
  const account = useSelector((state) => state.account);
  const knowledge = useSelector((state) => state.knowledge);

  // Load persona data
  useEffect(() => {
    if (
      account.active
            && knowledge.entities
            && knowledge.entities.length > 0
    ) {
      log.debug('Loading Persona Activity: ', knowledge.entities);

      // Calcualte target dates based off selection
      const lookback = activeChartData === '30-day' ? 60 : 180;

      const sDate = startDate || moment()
        .subtract(lookback, 'days')
        .format('YYYY-MM-DD');
      const eDate = endDate || moment().format('YYYY-MM-DD');

      // Sync Persona match data
      (async () => {
        // Get customer details
        const personaData = await getPersonas(
          account,
          sDate,
          eDate
        );

        console.log('Retrieved Persona Data:', personaData);

        // Update Personas
        if (personaData && personaData.personas) {
          // Clone existing personas
          const existingPersonas = knowledge.entities;

          // Iterate over updated persona data
          const updatedPersonas = [];
          for (let i = 0; i < personaData.personas.length; i++) {
            // Find matching persona
            const matchedPersona = _.find(
              existingPersonas,
              (existingPersona) => existingPersona.id === personaData.personas[i].id
            );

            // Update if matched
            if (matchedPersona) {
              // Update match count
              matchedPersona.matched = personaData.personas[i].total;

              // Add to new personas
              updatedPersonas.push(matchedPersona);
            }
          }
        }
      })();

      // Graph: Persona Activity
      (async () => {
        await getPersonaActivity(
          account,
          knowledge,
          sDate,
          eDate,
          personaActivityCallback
        );
      })();
    }
  }, [account, knowledge, startDate, endDate]);
}
